import { useEffect, useRef, useState } from "react";
import { Loader2, Check, Upload, Trash2, User as UserIcon } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui-kit/Dialog";
import { Button } from "@/components/ui-kit/Button";
import { Input, Label } from "@/components/ui-kit/Input";
import { PasswordStrength } from "@/components/auth/PasswordStrength";

function initials(name = "") {
  return name
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export function EditProfileDialog({ open, onOpenChange }) {
  const { user, updateUser } = useAuth();
  const fileRef = useRef(null);
  const [form, setForm] = useState(null);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open && user) {
      setForm({
        name: user.name || "",
        email: user.email || "",
        role: user.role || "",
        avatar: user.avatar || "",
      });
      setPw({ current_password: "", new_password: "", confirm: "" });
      setError("");
      setSaved(false);
    }
  }, [open, user]);

  if (!form) return null;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onAvatar = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      setError("Image must be under 500 KB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, avatar: reader.result }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setError("");
    if (!form.name.trim()) {
      setError("Display name cannot be empty.");
      return;
    }
    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      role: form.role.trim(),
      avatar: form.avatar || "",
    };
    if (pw.new_password) {
      if (pw.new_password !== pw.confirm) {
        setError("New password and confirmation do not match.");
        return;
      }
      if (!pw.current_password) {
        setError("Enter your current password to change it.");
        return;
      }
      payload.current_password = pw.current_password;
      payload.new_password = pw.new_password;
    }
    setSaving(true);
    try {
      const { data } = await api.patch("/users/me", payload);
      updateUser(data);
      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        onOpenChange(false);
      }, 800);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="edit-profile-dialog">
        <DialogTitle>Edit profile</DialogTitle>
        <DialogDescription>Update your name, photo, role, email and password.</DialogDescription>

        <div className="mt-5 space-y-5" data-testid="edit-profile-form">
          <div className="flex items-center gap-4">
            <span className="grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-full bg-primary/12 text-lg font-semibold text-primary">
              {form.avatar ? (
                <img src={form.avatar} alt="avatar" className="h-16 w-16 object-cover" data-testid="profile-avatar-preview" />
              ) : (
                initials(form.name) || <UserIcon className="h-6 w-6" />
              )}
            </span>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <input ref={fileRef} type="file" accept="image/*" onChange={onAvatar} data-testid="profile-avatar-upload" className="hidden" />
                <Button variant="secondary" size="sm" onClick={() => fileRef.current?.click()} data-testid="profile-avatar-button">
                  <Upload className="h-4 w-4" /> Upload image
                </Button>
                {form.avatar && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, avatar: "" })}
                    data-testid="profile-avatar-remove"
                    className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Remove
                  </button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">PNG or JPG, under 500 KB. Falls back to your initials.</p>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="profile-name">Display name</Label>
              <Input id="profile-name" data-testid="profile-name-input" value={form.name} onChange={set("name")} placeholder="Your name" />
            </div>
            <div>
              <Label htmlFor="profile-role">Role / title</Label>
              <Input id="profile-role" data-testid="profile-role-input" value={form.role} onChange={set("role")} placeholder="e.g. Managing Partner" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="profile-email">Email</Label>
              <Input id="profile-email" type="email" data-testid="profile-email-input" value={form.email} onChange={set("email")} placeholder="you@firm.com" />
            </div>
          </div>

          <div className="rounded-lg border border-border p-4">
            <p className="text-sm font-medium text-foreground">Change password</p>
            <p className="mb-3 text-xs text-muted-foreground">Leave blank to keep your current password.</p>
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <Label htmlFor="profile-current-pw">Current password</Label>
                <Input id="profile-current-pw" type="password" data-testid="profile-current-password-input" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="profile-new-pw">New password</Label>
                <Input id="profile-new-pw" type="password" data-testid="profile-new-password-input" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} />
                <PasswordStrength value={pw.new_password} />
              </div>
              <div>
                <Label htmlFor="profile-confirm-pw">Confirm new</Label>
                <Input id="profile-confirm-pw" type="password" data-testid="profile-confirm-password-input" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} />
              </div>
            </div>
          </div>

          {error && <p className="text-sm text-destructive" data-testid="profile-error">{error}</p>}

          <div className="flex items-center justify-end gap-3">
            {saved && <span className="inline-flex items-center gap-1 text-sm text-status-paid"><Check className="h-4 w-4" /> Saved</span>}
            <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} data-testid="profile-cancel">Cancel</Button>
            <Button size="sm" onClick={save} disabled={saving} data-testid="profile-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save profile
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

